// SPDX-License-Identifier: Apache-2.0
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.encodePackedIds = exports.decodePackedIds = exports.mapNFTs = exports.Tokens = exports.ErrIDAlreadyContained = void 0;
const ethers_1 = require("ethers");
const asset_1 = require("./asset");
const bigint_1 = require("../../utils/bigint");
const amount_1 = require("./amount");
exports.ErrIDAlreadyContained = new Error("given ID already contained in tokens");
class Tokens extends asset_1.Asset {
    constructor(v) {
        super();
        this.value = v.sort((a, b) => (a < b ? -1 : a == b ? 0 : 1));
        Tokens.requireUniquePositiveEntries(this.value);
    }
    // requireUniquePositiveEntries expects `v` to be sorted and asserts that every
    // entry in `v` is unique and a positive `bigint`. If the invariant does not
    // hold, an error is thrown.
    static requireUniquePositiveEntries(v) {
        for (let k = 0; k < v.length - 1; k++) {
            if (v[k] < 0n)
                throw new Error("invalid token id in token set");
            if (v[k] === v[k + 1])
                throw new Error("token entries not unique");
        }
    }
    toJSON() {
        return this.value.map((val) => {
            const arr = ethers_1.utils.arrayify(ethers_1.BigNumber.from(val));
            const offset = 32 - arr.length;
            const abi = new Uint8Array(32);
            abi.set(arr, offset);
            return ethers_1.utils.hexValue(abi);
        });
    }
    static fromJSON(idset) {
        const s = new Array();
        for (const id in idset) {
            s.push(BigInt(idset[id]));
        }
        return new Tokens(s);
    }
    typeTag() {
        return asset_1.TypeTags.Tokens;
    }
    asABI() {
        return ethers_1.utils.concat(this.value.map((v) => new amount_1.Amount(v).asABI()));
    }
    zero() {
        return this.value.length === 0;
    }
    clone() {
        const identity = (v) => {
            return v;
        };
        return new Tokens(this.value.map(identity));
    }
    cmp(asset) {
        if (!this.isCompatible(asset)) {
            return "uncomparable";
        }
        let res = ["lt", "eq", "gt"];
        let a = this.value;
        let b = asset.value;
        let cmp = 0;
        let swap = 1;
        if (b.length > a.length) {
            const t = a;
            a = b;
            b = t;
            swap = -1;
        }
        let bIdx = 0;
        for (let i = 0; i < a.length && bIdx < b.length; i++) {
            if (a[i] == b[bIdx])
                ++bIdx;
            else if (a[i] > b[bIdx])
                break; // short circuit.
        }
        if (bIdx !== b.length)
            return "uncomparable";
        if (a.length > b.length) {
            cmp = 1;
        }
        return res[cmp * swap + 1];
    }
    sub(asset) {
        asset_1.assertSubtractable(this, asset);
        let fillIdx = 0;
        let aIdx = 0;
        let bIdx = 0;
        let a = this.value;
        let b = asset.value;
        for (; aIdx < this.value.length && bIdx < b.length; aIdx++) {
            const x = this.value[aIdx];
            const y = b[bIdx];
            if (x !== y) {
                a[fillIdx] = x;
                fillIdx++;
            }
            else {
                bIdx++;
            }
        }
        let newLengthA = fillIdx;
        if (aIdx < this.value.length) {
            newLengthA = this.fillWithRemainders(a, fillIdx, aIdx);
        }
        a.splice(newLengthA);
        this.value = a;
    }
    // fillWithRemainders fills the given array `a` with the elements of `a`
    // starting from pos `from`, inserting them starting from pos `insertAt`.
    // It returns the length of the filled array.
    // It is assumed that `insertAt` < `from` holds.
    fillWithRemainders(a, insertAt, from) {
        for (; from < a.length; from++, insertAt++) {
            a[insertAt] = a[from];
        }
        return insertAt;
    }
    add(asset) {
        if (!this.isCompatible(asset)) {
            throw asset_1.ErrIncompatibleAssets;
        }
        asset.value.forEach(this.addID, this);
    }
    addID(id) {
        let targetPos = -1;
        this.value.forEach((v, i) => {
            if (v === id) {
                throw exports.ErrIDAlreadyContained;
            }
            if (v < id) {
                targetPos = i;
            }
            0;
        });
        if (targetPos === -1) {
            return this.insertAt(0, id);
        }
        // We found the last element smaller than our `id`, so we want to insert
        // the new `id` after the found one.
        this.insertAt(targetPos + 1, id);
    }
    insertAt(pos, id) {
        this.value = this.value
            .slice(0, pos)
            .concat([id], this.value.splice(pos));
    }
}
exports.Tokens = Tokens;
asset_1.registerAssetType(asset_1.TypeTags.Tokens, Tokens.fromJSON);
function mapNFTs(a, f) {
    return Array.from(a, ([token, asset]) => {
        if (asset instanceof Tokens) {
            return asset.value.map((id) => f(token, id));
        }
        else {
            return [];
        }
    }).flat();
}
exports.mapNFTs = mapNFTs;
// decodePackedIds receives a hex encoded string representing one or more ABI
// packed encoded `uint256` values.
function decodePackedIds(ids) {
    let idArr;
    if (!ids.startsWith("0x")) {
        idArr = ethers_1.utils.arrayify(`0x${ids}`);
    }
    else {
        idArr = ethers_1.utils.arrayify(ids);
    }
    if (idArr.length % 32 !== 0)
        throw new Error("received token array not divisible by 32");
    const size = idArr.length / 32;
    const res = Array.from({ length: size }, (_, i) => {
        return bigint_1.mkBigInt(idArr.slice(i * 32, i * 32 + 32).values(), 256, 8);
    });
    return res;
}
exports.decodePackedIds = decodePackedIds;
function encodePackedIds(ids) {
    return ethers_1.utils.hexlify(ethers_1.utils.concat(ids.map(amount_1.encodePackedAmount)));
}
exports.encodePackedIds = encodePackedIds;
//# sourceMappingURL=tokens.js.map